import { convertToUtcTime, formatNumeric } from '../../../../util';
import moment from 'moment';
import axios from 'axios';
import {
  dateFormat_DD_MM_YY,
  TransactionProductMapping,
  TransactionTypes
} from '../../../../util/GlobalConstant';
import {
  getCodesFromLC,
  getDataFromServer,
  isEmptyNullUndef,
  setCodesJSONToLC,
  getFormattedDate,
  getDataFromServerFullResponse
} from '../../../util';

export class TransactionHistoryModule {
  constructor(baseURL, httpHeaders) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
    this.cancelTokenSource = null;
  }

  // Method to cancel the current request
  cancelRequest = (message = 'Request canceled by user') => {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel(message);
    }
  };

  // Method to create a new cancel token
  createCancelToken = () => {
    // Cancel any existing request
    this.cancelRequest('New request initiated');

    // Create a new cancel token source
    this.cancelTokenSource = axios.CancelToken.source();
    return this.cancelTokenSource.token;
  };

  fetchTransactionHistory = async (payload = {}) => {
    // Cancel any existing request first
    if (this.cancelTokenSource) {
      this.cancelRequest('New request initiated');
    }

    const {
      customerKey,
      to,
      from,
      offset,
      limit,
      isScrolled,
      transactionType,
      alertId,
      downloadAll,
      isCM,
      moduleId,
      module,
      transactionId,
      counterpartyId,
      counterpartyName,
      accountId,
      caseId,
      sortOrder,
      zoneId
    } = payload;

    // Create a new cancel token for this request
    const cancelToken = this.createCancelToken();
    const url = `${this.baseURL}/customers/${customerKey}/transactionHistory`;
    const defaultTo = moment().format('DD/MM/YYYY');
    const defaultFrom = moment()
      .subtract(1, 'months')
      .add(1, 'days')
      .format('DD/MM/YYYY');

    // If from/to are already epoch millis (numbers), pass directly.
    // Otherwise convert the date string to UTC ISO for backward compatibility.
    const rawFrom = from ?? payload.filter?.from ?? defaultFrom;
    const rawTo = to ?? payload.filter?.to ?? defaultTo;
    const fromUtc =
      typeof rawFrom === 'number'
        ? rawFrom
        : convertToUtcTime(`${rawFrom} 00:00:00.000`);
    const toUtc =
      typeof rawTo === 'number'
        ? rawTo
        : convertToUtcTime(`${rawTo} 23:59:59.999`);

    let params = {
      ...(payload.filter || {}),
      from: fromUtc,
      to: toUtc,
      alertId,
      limit,
      offset,
      isCM,
      moduleId,
      ...(module && { module }),
      transactionId,
      counterpartyId,
      counterpartyName,
      accountId,
      ...(customerKey && { customerId: customerKey }),
      ...(caseId && { caseId }),
      ...(sortOrder && { sortOrder }),
      // FIN-12993: forward the viewer timezone so the CRS export renders transaction dates
      // the same way the grid does. Without this the param is dropped by the destructure
      // above and the CRS CSV always carries the raw stored wall-clock.
      ...(zoneId && { zoneId })
    };

    if (!isEmptyNullUndef(transactionType)) {
      params = {
        ...params,
        transactionType: Array.isArray(transactionType)
          ? transactionType.join(',')
          : transactionType + ','
      };
    }

    const response = await getDataFromServerFullResponse(
      url,
      this.httpHeaders,
      params,
      cancelToken
    );

    if (response.status !== 200 && response.status !== 202) {
      throw { message: 'Failed to fetch transaction history' };
    }

    if (downloadAll) {
      return response.data;
    }

    if (response.data) {
      const { transactions, count } = response.data;
      if (Array.isArray(transactions)) {
        const formattedTransactions = transactions.map((o) => {
          const {
            localCurrencyEquivalent,
            isCredit,
            transactionDate,
            destCountryCode,
            accountId,
            amount,
            transactionTypeDescription,
            creditLimit,
            transactionCurrencyAmount,
            accountCurrency,
            transactionCurrency
          } = o;

          return {
            ...o,
            destination: destCountryCode,
            source: `${accountId}`,
            type: transactionTypeDescription,
            credit_debit: isCredit != null ? (isCredit ? 'CR' : 'DR') : null,
            amount: amount
              ? `$${formatNumeric(Number(amount).toFixed(2))}`
              : '-',
            accountCurrency: amount ? accountCurrency : '-',
            transactionCurrency: amount ? transactionCurrency : '-',
            date: getFormattedDate(transactionDate, dateFormat_DD_MM_YY),
            creditLimit: `$${formatNumeric(Number(creditLimit).toFixed(2))}`,
            transactionCurrencyAmount: transactionCurrencyAmount
              ? `$${formatNumeric(
                  Number(transactionCurrencyAmount).toFixed(2)
                )}`
              : '-',
            localCurrencyEquivalent: `$${formatNumeric(
              Number(localCurrencyEquivalent).toFixed(2)
            )}`
          };
        });

        return {
          associated_transactions: formattedTransactions,
          isScrolled,
          customerId: customerKey,
          count
        };
      }
    }

    return {
      associated_transactions: [],
      isScrolled,
      customerId: customerKey
    };
  };

  fetchTransactionProductMapping = async () => {
    const transactionProductMappingData = [];
    const data = getCodesFromLC(TransactionProductMapping);
    if (data.length === 0) {
      const params = {
        codeType: 'TRANSACTION_PRODUCT_MAPPING'
      };
      const productMapResponse = await getDataFromServer(
        `${this.baseURL}/codes`,
        this.httpHeaders,
        params
      );
      await setCodesJSONToLC(TransactionProductMapping, productMapResponse);
      return productMapResponse;
    }
    return transactionProductMappingData;
  };

  fetchTransactionTypes = async () => {
    const transactionTypesData = [];
    const data = getCodesFromLC(TransactionTypes);
    if (data.length === 0) {
      const params = {
        codeType: 'TRANSACTION_TYPE'
      };
      const transactionTypesResponse = await getDataFromServer(
        `${this.baseURL}/codes`,
        this.httpHeaders,
        params
      );
      await setCodesJSONToLC(TransactionTypes, transactionTypesResponse);
      return transactionTypesResponse;
    }
    return transactionTypesData;
  };
}
